import { useQuery } from '@apollo/client';
import { faEllipsis, faFileArrowDown } from '@fortawesome/pro-regular-svg-icons';
import * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import Button from '../../../core/components/Button.tsx';
import DropdownMenu from '../../../core/components/DropdownMenu.tsx';
import { withIcon } from '../../../core/components/Icon.tsx';
import { SettingsCard } from '../../../core/components/SettingsFormCard.tsx';
import FieldsConfig from '../FieldsConfig.tsx';
import { ConfigChannel, ConfigPart, ConfigVersion, formatConfigDate, getChangedPartLabels } from './changes.ts';
import ImportConfigModal from './ImportConfigModal.tsx';
import { GET_CHANNEL_CONFIG_VERSIONS } from './queries.ts';
import { CompareVersionModal, DownloadVersionModal, RestoreVersionModal } from './VersionModals.tsx';

const DownloadIcon = withIcon(faFileArrowDown);
const EllipsisIcon = withIcon(faEllipsis);

type Dialog = { type: 'import' } | { type: 'download' | 'compare' | 'restore'; number: number } | null;

interface VersionTextProps {
    version: ConfigVersion;
    parts: ConfigPart[];
}

const VersionText = ({ version, parts }: VersionTextProps) => {
    const { t } = useTranslation('config');
    const changeCount = version.changedPaths.length;
    const meta = [
        version.createdByName || version.createdByEmail || t('configPage.history.unknownAuthor'),
        formatConfigDate(version.createdAt),
        changeCount > 0 && t('configPage.history.changeCount', { count: changeCount }),
        t(`configPage.history.sources.${version.source}`, {
            number: version.restoredFromNumber,
            name: version.sourceName,
            defaultValue: '',
        }),
    ];
    return (
        <span className="flex min-w-0 flex-1 flex-col">
            <span className="font-medium text-content-strong">
                {changeCount === 0
                    ? t('configPage.history.firstVersion')
                    : getChangedPartLabels(parts, version.changedPaths).join(', ')}
            </span>
            <span className="text-content-medium">{meta.filter(Boolean).join(' · ')}</span>
        </span>
    );
};

interface ConfigHistoryProps {
    channel: ConfigChannel;
}

const ConfigHistory = ({ channel }: ConfigHistoryProps) => {
    const { t } = useTranslation('config');
    const [dialog, setDialog] = useState<Dialog>(null);
    const { data, error } = useQuery(GET_CHANNEL_CONFIG_VERSIONS, {
        variables: { documentType: channel.documentType, channelId: channel.channelId },
        fetchPolicy: 'cache-and-network',
    });
    const versions: ConfigVersion[] = data?.channelConfigVersions ?? [];
    const noVersionKey = error
        ? 'configPage.history.versionsError'
        : data
          ? 'configPage.history.noVersions'
          : 'configPage.history.loadingVersions';
    const [current, ...earlier] = versions;
    const close = () => setDialog(null);

    return (
        <>
            <SettingsCard
                className="max-w-none"
                title={t('configPage.history.title')}
                controls={
                    <Button onClick={() => setDialog({ type: 'import' })}>{t('configPage.history.importFile')}</Button>
                }
            >
                {current ? (
                    <FieldsConfig.Section title={t('configPage.history.currentVersion', { number: current.number })}>
                        <FieldsConfig.Card>
                            <div className="flex items-center gap-4 px-4 py-3 text-sm">
                                <VersionText version={current} parts={channel.parts} />
                                <Button
                                    className="flex items-center gap-2"
                                    onClick={() => setDialog({ type: 'download', number: current.number })}
                                >
                                    <DownloadIcon />
                                    {t('configPage.history.download.action')}
                                </Button>
                            </div>
                        </FieldsConfig.Card>
                    </FieldsConfig.Section>
                ) : (
                    <span className="text-sm text-content-medium">{t(noVersionKey)}</span>
                )}
                {earlier.length > 0 && (
                    <FieldsConfig.Section title={t('configPage.history.earlierVersions')}>
                        <FieldsConfig.Card hasFullWidthDividers>
                            {earlier.map((version) => (
                                <div key={version.number} className="flex items-center gap-4 px-4 py-3 text-sm">
                                    <span className="w-8 flex-none tabular-nums text-content-medium">
                                        {version.number}
                                    </span>
                                    <VersionText version={version} parts={channel.parts} />
                                    <span className="flex flex-none items-center gap-1">
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            title={t('configPage.history.download.title', { number: version.number })}
                                            aria-label={t('configPage.history.download.title', {
                                                number: version.number,
                                            })}
                                            onClick={() => setDialog({ type: 'download', number: version.number })}
                                        >
                                            <DownloadIcon />
                                        </Button>
                                        <DropdownMenu>
                                            <DropdownMenu.Trigger asChild>
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    aria-label={t('configPage.history.moreActions', {
                                                        number: version.number,
                                                    })}
                                                >
                                                    <EllipsisIcon />
                                                </Button>
                                            </DropdownMenu.Trigger>
                                            <DropdownMenu.Content align="end" className="min-w-48">
                                                <DropdownMenu.Item
                                                    onSelect={() =>
                                                        setDialog({ type: 'compare', number: version.number })
                                                    }
                                                >
                                                    {t('configPage.history.compare.action')}
                                                </DropdownMenu.Item>
                                                <DropdownMenu.Item
                                                    onSelect={() =>
                                                        setDialog({ type: 'restore', number: version.number })
                                                    }
                                                >
                                                    {t('configPage.history.restore.action')}
                                                </DropdownMenu.Item>
                                            </DropdownMenu.Content>
                                        </DropdownMenu>
                                    </span>
                                </div>
                            ))}
                        </FieldsConfig.Card>
                    </FieldsConfig.Section>
                )}
            </SettingsCard>

            {dialog?.type === 'import' && <ImportConfigModal channel={channel} onClose={close} />}
            {dialog?.type === 'download' && (
                <DownloadVersionModal channel={channel} number={dialog.number} onClose={close} />
            )}
            {dialog?.type === 'compare' && current && (
                <CompareVersionModal
                    channel={channel}
                    number={dialog.number}
                    currentNumber={current.number}
                    onRestore={() => setDialog({ type: 'restore', number: dialog.number })}
                    onClose={close}
                />
            )}
            {dialog?.type === 'restore' && (
                <RestoreVersionModal channel={channel} number={dialog.number} onClose={close} />
            )}
        </>
    );
};

export default ConfigHistory;
